import { useCallback, useEffect, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, Trash2, Users, Settings2, BadgeCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const LEAVE_TYPES = [
  { key: "conges_payes", label: "Congés payés", def: 25 },
  { key: "rtt", label: "RTT", def: 12 },
  { key: "maladie", label: "Maladie", def: 15 },
];

function fmtDays(v) {
  const n = Number(v || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(".", ",");
}

export default function RHEmployees() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(baseForm());
  const [editing, setEditing] = useState(null); // employee for full edit
  const [savingEdit, setSavingEdit] = useState(false);
  const [techDocTypes, setTechDocTypes] = useState([]);

  function baseForm() {
    return { email: "", password: "", full_name: "", poste: "", phone: "", date_embauche: "", date_naissance: "", salaire_base: "", soldes: { conges_payes: 25, rtt: 12, maladie: 15 }, assigned_tech_docs: [] };
  }

  const load = useCallback(async () => {
    try { const { data } = await api.get("/users"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => {
    load();
    api.get("/tech-docs/types")
      .then(({ data }) => setTechDocTypes(data))
      .catch((err) => console.warn("Employees.loadTechDocTypes failed", err));
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        role: "employe",
        salaire_base: form.salaire_base === "" ? null : parseFloat(form.salaire_base) || null,
        soldes: Object.fromEntries(LEAVE_TYPES.map((t) => [t.key, parseFloat(form.soldes[t.key]) || 0])),
        assigned_tech_docs: form.assigned_tech_docs || [],
      };
      await api.post("/users", payload);
      toast.success("Employé créé");
      setOpen(false);
      setForm(baseForm());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cet employé ?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Employé supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveEditing = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const soldes = Object.fromEntries(LEAVE_TYPES.map((t) => [t.key, parseFloat(editing.soldes?.[t.key]) || 0]));
      const payload = {
        full_name: (editing.full_name || "").trim(),
        email: (editing.email || "").trim().toLowerCase(),
        poste: (editing.poste || "").trim() || null,
        phone: (editing.phone || "").trim() || null,
        date_embauche: editing.date_embauche || null,
        date_naissance: editing.date_naissance || null,
        salaire_base: editing.salaire_base === "" || editing.salaire_base == null ? null : parseFloat(editing.salaire_base) || null,
        soldes,
        assigned_tech_docs: editing.assigned_tech_docs || [],
      };
      if (editing._new_password && editing._new_password.trim()) {
        payload.password = editing._new_password.trim();
      }
      await api.patch(`/users/${editing.id}`, payload);
      toast.success(`Fiche mise à jour pour ${payload.full_name || editing.full_name}`);
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingEdit(false); }
  };

  return (
    <div>
      <PageHeader
        title="Employés"
        subtitle="Gérez les comptes employés et leurs soldes de congés initiaux."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-employee-button" className="bg-[#0066FF] hover:bg-[#0052CC]"><UserPlus className="h-4 w-4 mr-2" />Nouvel employé</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Créer un employé</DialogTitle>
                <DialogDescription>Renseignez les informations de base, les soldes de congés et les documents professionnels à fournir.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nom complet</Label><Input data-testid="emp-name-input" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
                  <div><Label>Poste</Label><Input data-testid="emp-poste-input" value={form.poste} onChange={(e) => setForm({ ...form, poste: e.target.value })} placeholder="Ex : Technicien" /></div>
                </div>
                <div><Label>Email</Label><Input data-testid="emp-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Téléphone <span className="text-slate-400 text-xs font-normal">(facultatif)</span></Label><Input data-testid="emp-phone-input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+33 6 12 34 56 78" /></div>
                <div><Label>Mot de passe temporaire</Label><Input data-testid="emp-password-input" type="text" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Date d'embauche</Label><Input data-testid="emp-embauche-input" type="date" value={form.date_embauche} onChange={(e) => setForm({ ...form, date_embauche: e.target.value })} /></div>
                  <div><Label>Date de naissance</Label><Input data-testid="emp-birthday-input" type="date" value={form.date_naissance} onChange={(e) => setForm({ ...form, date_naissance: e.target.value })} /></div>
                </div>
                <div>
                  <Label>Salaire mensuel brut (€)</Label>
                  <Input
                    data-testid="emp-salary-input"
                    type="number" min={0} step="0.01"
                    value={form.salaire_base}
                    onChange={(e) => setForm({ ...form, salaire_base: e.target.value })}
                    placeholder="Ex : 2450.00"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Nécessaire pour générer les attestations de salaire.</p>
                </div>
                <div className="pt-2 border-t border-slate-100">
                  <div className="text-sm font-semibold text-slate-700 mb-2">Soldes initiaux (jours)</div>
                  <div className="grid grid-cols-3 gap-3">
                    {LEAVE_TYPES.map((t) => (
                      <div key={t.key}>
                        <Label className="text-xs">{t.label}</Label>
                        <Input
                          data-testid={`emp-solde-${t.key}-input`}
                          type="number" min={0} max={365} step={0.5}
                          value={form.soldes[t.key]}
                          onChange={(e) => setForm({ ...form, soldes: { ...form.soldes, [t.key]: e.target.value } })}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-100">
                  <div className="text-sm font-semibold text-slate-700 mb-2">Documents professionnels attribués</div>
                  <p className="text-[11px] text-slate-500 mb-2">Cochez les documents que cet employé doit fournir. Il ne verra que ceux-ci dans son espace.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {techDocTypes.map((t) => {
                      const checked = (form.assigned_tech_docs || []).includes(t.doc_type);
                      return (
                        <label key={t.doc_type} className="flex items-center gap-2 text-sm cursor-pointer rounded-md border border-slate-200 px-3 py-2 hover:bg-slate-50">
                          <Checkbox
                            data-testid={`emp-td-${t.doc_type}`}
                            checked={checked}
                            onCheckedChange={(v) => {
                              const cur = new Set(form.assigned_tech_docs || []);
                              if (v) cur.add(t.doc_type); else cur.delete(t.doc_type);
                              setForm({ ...form, assigned_tech_docs: Array.from(cur) });
                            }}
                          />
                          <span>{t.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                  <Button data-testid="submit-employee-button" type="submit" disabled={saving} className="bg-[#0066FF] hover:bg-[#0052CC]">{saving ? "…" : "Créer"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="border-slate-200 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">Aucun employé enregistré</p>
          </div>
        ) : (
          <>
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Poste</TableHead>
                <TableHead>Embauche</TableHead>
                <TableHead>CP</TableHead>
                <TableHead>RTT</TableHead>
                <TableHead>Maladie</TableHead>
                <TableHead>Salaire</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((u) => (
                <TableRow key={u.id} data-testid={`employee-row-${u.id}`}>
                  <TableCell>
                    <div className="font-medium">{u.full_name}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </TableCell>
                  <TableCell>{u.poste || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-500">{u.date_embauche || "—"}</TableCell>
                  <TableCell><span className="font-medium">{fmtDays(u.soldes?.conges_payes)}</span> j</TableCell>
                  <TableCell><span className="font-medium">{fmtDays(u.soldes?.rtt)}</span> j</TableCell>
                  <TableCell><span className="font-medium">{fmtDays(u.soldes?.maladie)}</span> j</TableCell>
                  <TableCell className="text-sm">
                    {u.salaire_base ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(u.salaire_base) : <span className="text-slate-400">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        asChild
                        data-testid={`docs-pro-${u.id}`}
                        size="sm" variant="outline"
                        className="text-[#0066FF] border-[#0066FF]/20 hover:bg-[#0066FF]/5"
                      >
                        <Link to={`/rh/documents-pro/${u.id}`}><BadgeCheck className="h-3.5 w-3.5 mr-1" />Docs pro</Link>
                      </Button>
                      <Button
                        data-testid={`edit-solde-${u.id}`}
                        size="sm" variant="outline"
                        onClick={() => setEditing({ ...u, soldes: { ...(u.soldes || {}) }, assigned_tech_docs: [...(u.assigned_tech_docs || [])] })}
                      >
                        <Settings2 className="h-3.5 w-3.5 mr-1" />Modifier
                      </Button>
                      <Button data-testid={`delete-employee-${u.id}`} size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => del(u.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <div className="md:hidden divide-y divide-slate-100">
            {items.map((u) => (
              <div key={u.id} className="p-4" data-testid={`employee-card-${u.id}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{u.full_name}</div>
                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                    <div className="text-xs text-slate-600 mt-1">{u.poste || "—"}</div>
                  </div>
                  {u.salaire_base ? (
                    <div className="shrink-0 text-right">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Salaire</div>
                      <div className="text-sm font-semibold text-slate-900">
                        {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(u.salaire_base)}
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="grid grid-cols-4 gap-2 text-center text-xs mb-3">
                  <div className="rounded-md bg-slate-50 py-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">CP</div>
                    <div className="font-semibold text-slate-900">{fmtDays(u.soldes?.conges_payes)}<span className="text-slate-400 font-normal"> j</span></div>
                  </div>
                  <div className="rounded-md bg-slate-50 py-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">RTT</div>
                    <div className="font-semibold text-slate-900">{fmtDays(u.soldes?.rtt)}<span className="text-slate-400 font-normal"> j</span></div>
                  </div>
                  <div className="rounded-md bg-slate-50 py-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Mal.</div>
                    <div className="font-semibold text-slate-900">{fmtDays(u.soldes?.maladie)}<span className="text-slate-400 font-normal"> j</span></div>
                  </div>
                  <div className="rounded-md bg-slate-50 py-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Emb.</div>
                    <div className="font-semibold text-slate-900 text-[11px] leading-tight pt-0.5">{u.date_embauche || "—"}</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Button asChild data-testid={`docs-pro-m-${u.id}`} size="sm" variant="outline"
                    className="text-[#0066FF] border-[#0066FF]/20 hover:bg-[#0066FF]/5 w-full">
                    <Link to={`/rh/documents-pro/${u.id}`}><BadgeCheck className="h-3.5 w-3.5 mr-1" />Docs</Link>
                  </Button>
                  <Button data-testid={`edit-solde-m-${u.id}`} size="sm" variant="outline" className="w-full"
                    onClick={() => setEditing({ ...u, soldes: { ...(u.soldes || {}) }, assigned_tech_docs: [...(u.assigned_tech_docs || [])] })}>
                    <Settings2 className="h-3.5 w-3.5 mr-1" />Modifier
                  </Button>
                  <Button data-testid={`delete-employee-m-${u.id}`} size="sm" variant="outline"
                    className="text-rose-600 border-rose-200 hover:bg-rose-50 w-full" onClick={() => del(u.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier — {editing?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {editing && (
              <div className="flex items-center gap-4 rounded-lg border border-slate-200 p-3 bg-slate-50/50">
                <PhotoPicker userId={editing.id} name={editing.full_name} onUploaded={load} />
                <div className="text-sm">
                  <div className="font-medium text-slate-900">Photo de profil</div>
                  <div className="text-xs text-slate-500">Cliquez sur le "+" pour changer. Utilisée dans l'onglet Équipe et le trombinoscope.</div>
                </div>
              </div>
            )}

            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Identité</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nom complet</Label>
                  <Input
                    data-testid="edit-name-input"
                    value={editing?.full_name || ""}
                    onChange={(e) => setEditing({ ...editing, full_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Adresse e-mail</Label>
                  <Input
                    data-testid="edit-email-input"
                    type="email"
                    value={editing?.email || ""}
                    onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Poste & dates</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Poste</Label>
                  <Input
                    data-testid="edit-poste-input"
                    value={editing?.poste || ""}
                    onChange={(e) => setEditing({ ...editing, poste: e.target.value })}
                    placeholder="Ex : Technicien"
                  />
                </div>
                <div>
                  <Label className="text-xs">Date d'embauche</Label>
                  <Input
                    data-testid="edit-embauche-input"
                    type="date"
                    value={editing?.date_embauche || ""}
                    onChange={(e) => setEditing({ ...editing, date_embauche: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Date de naissance</Label>
                  <Input
                    data-testid="edit-birthday-input"
                    type="date"
                    value={editing?.date_naissance || ""}
                    onChange={(e) => setEditing({ ...editing, date_naissance: e.target.value })}
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs">Téléphone</Label>
                <Input
                  data-testid="edit-phone-input"
                  type="tel"
                  value={editing?.phone || ""}
                  onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                  placeholder="+33 6 12 34 56 78"
                />
              </div>
              <div className="mt-3">
                <Label className="text-xs">Salaire mensuel brut (€)</Label>
                <Input
                  data-testid="edit-salary-input"
                  type="number" min={0} step="0.01"
                  value={editing?.salaire_base ?? ""}
                  onChange={(e) => setEditing({ ...editing, salaire_base: e.target.value })}
                  placeholder="Ex : 2450.00"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Nécessaire pour que l'employé puisse demander une attestation de salaire.
                </p>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">Seuls le jour et le mois de la date de naissance sont visibles par les collègues.</p>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Soldes de congés</div>
              <div className="grid grid-cols-3 gap-3">
                {editing && LEAVE_TYPES.map((t) => (
                  <div key={t.key}>
                    <Label className="text-xs">{t.label}</Label>
                    <Input
                      data-testid={`edit-solde-${t.key}-input`}
                      type="number" min={0} max={365} step={0.5}
                      value={editing.soldes?.[t.key] ?? 0}
                      onChange={(e) => setEditing({ ...editing, soldes: { ...editing.soldes, [t.key]: e.target.value } })}
                    />
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-1">Les demandes en attente et validées sont automatiquement décomptées lors du calcul de la disponibilité.</p>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Documents professionnels attribués</div>
              <p className="text-[11px] text-slate-500 mb-2">Sélectionnez les documents que cet employé doit fournir. Seuls ces documents apparaîtront dans son espace personnel.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {editing && techDocTypes.map((t) => {
                  const checked = (editing.assigned_tech_docs || []).includes(t.doc_type);
                  return (
                    <label key={t.doc_type} className="flex items-center gap-2 text-sm cursor-pointer rounded-md border border-slate-200 px-3 py-2 hover:bg-slate-50">
                      <Checkbox
                        data-testid={`edit-td-${t.doc_type}`}
                        checked={checked}
                        onCheckedChange={(v) => {
                          const cur = new Set(editing.assigned_tech_docs || []);
                          if (v) cur.add(t.doc_type); else cur.delete(t.doc_type);
                          setEditing({ ...editing, assigned_tech_docs: Array.from(cur) });
                        }}
                      />
                      <span>{t.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Réinitialiser le mot de passe</div>
              <Input
                data-testid="edit-password-input"
                type="text"
                autoComplete="new-password"
                placeholder="Laissez vide pour ne pas changer"
                value={editing?._new_password || ""}
                onChange={(e) => setEditing({ ...editing, _new_password: e.target.value })}
              />
              <p className="text-[11px] text-slate-500 mt-1">Minimum 8 caractères. Communiquez le nouveau mot de passe à l'employé — il pourra le changer depuis Mon profil.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button data-testid="save-solde-button" onClick={saveEditing} disabled={savingEdit} className="bg-[#0066FF] hover:bg-[#0052CC]">
              {savingEdit ? "…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotoPicker({ userId, name, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const src = `${API}/users/${userId}/photo?_=${reloadKey}`;
  const [blobUrl, setBlobUrl] = useState(null);
  const inputId = `photo-${userId}`;

  useEffect(() => {
    let revoke;
    let cancelled = false;
    async function fetchPhoto() {
      setBlobUrl(null);
      try {
        const res = await api.get(`/users/${userId}/photo`, { responseType: "blob" });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        revoke = url; setBlobUrl(url);
      } catch (err) {
        if (err?.response?.status !== 404) {
          console.warn("Employees.PhotoPicker fetch failed", err);
        }
      }
    }
    fetchPhoto();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [userId, reloadKey]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(f.type)) { toast.error("Format invalide (PNG/JPG/WEBP)"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image trop volumineuse (max 5 Mo)"); return; }
    setPreview(URL.createObjectURL(f));
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/users/${userId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo mise à jour");
      setReloadKey((k) => k + 1);
      onUploaded?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); setPreview(null); e.target.value = ""; }
  };

  const initials = (name || "?").split(" ").filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="relative">
      <label htmlFor={inputId} className="cursor-pointer block">
        <div className="h-16 w-16 rounded-full ring-2 ring-slate-100 overflow-hidden bg-slate-100 flex items-center justify-center text-slate-500 font-bold">
          {preview ? (
            <img src={preview} alt="preview" className="h-full w-full object-cover" />
          ) : blobUrl ? (
            <img src={blobUrl} alt={name} className="h-full w-full object-cover" />
          ) : (
            <span>{initials}</span>
          )}
        </div>
        <div className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-[#0066FF] text-white flex items-center justify-center border-2 border-white text-xs">+</div>
      </label>
      <input id={inputId} data-testid={`photo-input-${userId}`} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} disabled={busy} />
    </div>
  );
}
